import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('authenticating'); // authenticating, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const hasAttempted = useRef(false); // Prevents React StrictMode double-firing

  useEffect(() => {
    const processAuthCode = async () => {
      if (hasAttempted.current) return;
      hasAttempted.current = true;

      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const stateParam = searchParams.get('state');

      if (error) {
        setStatus('error');
        setErrorMessage('Google Authorization was denied or failed.');
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code found in the URL.');
        return;
      }

      // Decrypt the routing payload
      let parsedState = {};
      if (stateParam) {
        try {
          parsedState = JSON.parse(decodeURIComponent(stateParam));
        } catch (err) {
          console.warn("System Warning: Failed to parse Google OAuth state parameter.", err);
        }
      }

      try {
        const functions = getFunctions();
        const exchangeGoogleAuthCode = httpsCallable(functions, 'exchangeGoogleAuthCode');
        
        // Dynamically grab the exact URL the user is currently on
        const currentRedirectUri = `${window.location.origin}/integrations/google/callback`;

        // We send BOTH camelCase and snake_case just in case the backend expects a specific format
        await exchangeGoogleAuthCode({ 
          code: code,
          redirectUri: currentRedirectUri,
          redirect_uri: currentRedirectUri,
          tenantId: parsedState.tenantId, 
          storeId: parsedState.storeId    
        });

        setStatus('success');
        
        setTimeout(() => {
          navigate('/calendar');
        }, 3000);

      } catch (err) {
        console.error("Token Exchange Error:", err);
        setStatus('error');
        setErrorMessage('Failed to securely connect your calendar. Please try again.');
      }
    };

    processAuthCode();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 font-sans">
      <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 shadow-xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-500 rounded-sm">
        
        {status === 'authenticating' && (
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-700 border-t-emerald-500 rounded-full animate-spin mb-6"></div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest mb-2">Securing Connection</h2>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Exchanging cryptographic tokens with Google...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center text-emerald-600 dark:text-emerald-400">
            <i className="fa-solid fa-circle-check text-5xl mb-6"></i>
            <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest mb-2">Calendar Connected</h2>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-6">Your schedule is now securely synced.</p>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest animate-pulse">Redirecting to dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center text-rose-500">
            <i className="fa-solid fa-triangle-exclamation text-5xl mb-6"></i>
            <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-widest mb-2">Connection Failed</h2>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-6">{errorMessage}</p>
            <button 
              onClick={() => navigate('/calendar')}
              className="bg-slate-900 dark:bg-slate-700 text-white text-[10px] font-bold py-3 px-6 uppercase tracking-widest hover:bg-slate-800 transition-colors rounded-sm shadow-sm"
            >
              Return to Calendar
            </button>
          </div>
        )}

      </div>
    </div>
  );
}